/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { alarmForwardingColumns } from "../../../../components/Shared/Table/Columns";
import { useStoreDispatch } from "../../../../store/hooks";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  useCreateSnmpGroupMutation,
  useDeleteSnmpGroupMutation,
  useGetNorthboundGroupsQuery,
  useGetSNMPGroupMutation,
  useUpdateSnmpGroupMutation,
} from "../../../../store/api/admin/adminApiSlice";
import { ISnmpGroup } from "../../../../store/api/admin/types";
import { isCreateSnmpSelector, selectedSnmpGroupSelector } from "../../../../store/api/admin/selectors";
import { setBasicDialogOpenFor, setErrorMsg, setOpenToast } from "../../../../store/helpers/helperReducers";
import { errorMessage, titleCase } from "../../../../utils/helpers";
import { snmpGroupPayload } from "../../../helpers";
import { isValidPassword } from "../../../../utils/validations";
import { AuthenticationOptions, PrivacyOptions } from "../../../../globals/constants";
import { validateIPAddressList } from "../../AccessControls/Authentication/helpers";
import { encodeParam } from "../../../../store/helpers/helpers";

interface IPayload {
  [key: string]: string;
}

export const useAddEditAlarmForwarding = () => {
  const dispatch = useStoreDispatch();
  const selectedSnmpGroup = useSelector(selectedSnmpGroupSelector);
  const isCreateSNMPGroup = useSelector(isCreateSnmpSelector);
  const [formData, setFormData] = useState<ISnmpGroup>(selectedSnmpGroup);
  const [errMsg, setErrMsg] = useState<IPayload>({});
  const [version, setVersion] = useState("V2C");
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [isPrivChecked, setIsPrivChecked] = useState(false);
  const [authPassword, setAuthPassword] = useState("");
  const [privPassword, setPrivPassword] = useState("");
  const [authConfirmPassword, setAuthConfirmPassword] = useState("");
  const [privConfirmPassword, setPrivConfirmPassword] = useState("");
  const [selectedRowIDs, setSelectedRowIDs] = useState<any[]>([]);

  const args = {
    filters: "",
    deviceKey: "",
    search: "",
  };

  const { data, isLoading, refetch } = useGetNorthboundGroupsQuery(args);
  const [getSnmpGroup] = useGetSNMPGroupMutation();
  const [deleteSnmpGroup] = useDeleteSnmpGroupMutation();
  const [selectedGroup, setSelectedGroup] = useState<any>(null);

  const [createSnmpGroup] = useCreateSnmpGroupMutation();
  const [updateSnmpGroup] = useUpdateSnmpGroupMutation();

  useEffect(() => {
    if (!isCreateSNMPGroup && selectedSnmpGroup) {
      setVersion(selectedSnmpGroup?.version === 2 ? "V2C" : "V3");
    }
  }, [isCreateSNMPGroup, selectedSnmpGroup]);

  const getValue = (val: string) => {
    let checkVal = false;
    checkVal = val?.includes(",");
    return checkVal;
  };

  useEffect(() => {
    setSelectedRowIDs([]);
  }, [data])

  const updateErr = (newErr: IPayload) => {
    setErrMsg(newErr);
    dispatch(setErrorMsg(newErr));
  };

  const onBlurHandler = (event: any) => {
    const { name, value } = event?.target;
    if (name === "name") {
      if (!value?.trim()) {
        updateErr({ ...errMsg, [name]: `Name is required!` });
        return;
      }
      let matchCount: any = [];
      matchCount = data?.filter((el: ISnmpGroup) => el?.name?.toLowerCase() === value?.toLowerCase());
      if (matchCount?.length > 0) {
        updateErr({ [name]: `Invalid Name, Please use another Name. Already available in SNMP Group.` });
        return;
      }
    }
    if (name === "ipAddressesList") {
      let newValue: any;
      if (getValue(value)) {
        newValue = value?.split(",");
        if (newValue?.length > 0) {
          newValue?.forEach((emailKey: any) => {
            if (emailKey?.includes(" ")) {
              updateErr({ [name]: `Invalid Hostname, Please remove empty spaces` });
              return;
            }
            const errorMessages = validateIPAddressList({ ipAddressesList: emailKey }, formData?.enabled);
            if (Object.keys(errorMessages)?.length > 0) {
              updateErr({ ...errMsg, ...errorMessages });
              return;
            }
          });
        }
      } else {
        const errorMessages = validateIPAddressList({ ipAddressesList: value }, formData?.enabled);
        if (Object.keys(errorMessages)?.length > 0) {
          updateErr({ ...errMsg, ...errorMessages });
          return;
        }
      }
    }
    if (name === "heartbeatInterval" && formData?.enableHeartbeat) {
      if (!value?.toString()?.trim()) {
        updateErr({ ...errMsg, [name]: `Heartbeat Interval is required!` });
        return;
      }
      const num = Number(value);
      if (isNaN(num) || num < 0 || num > 3600) {
        updateErr({ ...errMsg, [name]: `Value should be between 0 and 3600` });
        return;
      }
    }
    if (name === "community" && version === "V2C") {
      if (!value?.trim()) {
        updateErr({ ...errMsg, [name]: `Community is required!` });
        return;
      }
    }
    if ((name === "authPassword" && isAuthChecked) || (name === "authConfirmPassword" && isAuthChecked)) {
      if (!!value) {
        const isValidPass = isValidPassword(value);
        if (!isValidPass) {
          updateErr({ [name]: `Password is invalid. It must meet the specified criteria!` });
        }
        else updateErr({ [name]: `` });
      } else {
        updateErr({ [name]: `Password is required!` });
      }
    }
    if ((name === "privPassword" && isPrivChecked) || (name === "privConfirmPassword" && isPrivChecked)) {
      if (!!value) {
        const isValidPass = isValidPassword(value);
        if (!isValidPass) {
          updateErr({ [name]: `Password is invalid. It must meet the specified criteria!` });
        }
        else updateErr({ [name]: `` });
      } else {
        updateErr({ [name]: `Password is required!` });
      }
    }
  };

  const getSelectedSnmpGroup = async (text: string) => {
    if (text) {
      let args = { group: text };
      try {
        let result: any;
        result = await getSnmpGroup(args);
        if (result?.error) throw result?.error;
        if (result.data?.status === 200) {
          setSelectedGroup(result?.data?.data);
        }
      } catch (error) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  const handleDeleteSnmpGroup = async (text: string, actionName: string) => {
    if (text) {
      try {
        let result: any;
        if (actionName.match("delete")) {
          result = await deleteSnmpGroup(encodeParam(text));
          if (result?.error) throw result?.error;
          if (result.data?.status === 200) {
            dispatch(
              setOpenToast({
                openToast: true,
                toastType: "success",
                message: `The ${text} has been deleted .`,
              })
            );
            refetch();
          }
        }
      } catch (error) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  const handleValidateAlarmGroup = () => {
    let valid: any = true;
    if (version === "V3" && formData?.authProtocol !== selectedSnmpGroup?.authProtocol && formData?.authProtocol?.toLowerCase() !== "no_auth" && !authPassword) {
      setErrMsg({ ...errMsg, authProtocol: `Authentication Protocol is selected and Password length is less than 8.` });
      valid = false;
    }
    if (version === "V3" && formData?.privProtocol !== selectedSnmpGroup?.privProtocol && formData?.privProtocol?.toLowerCase() !== "no_priv" && !privPassword) {
      setErrMsg({ ...errMsg, privProtocol: `Privacy Protocol is selected and Password length is less than 8.` });
      valid = false;
    }
    if (version === "V2C" && formData?.community === "") {
      setErrMsg({ ...errMsg, community: `Community is Required!!` });
      valid = false;
    }
    if (version === "V3" && formData?.userName === "") {
      setErrMsg({ ...errMsg, userName: `userName is Required!!` });
      valid = false;
    }
    if (formData?.ipAddressesList === "") {
      setErrMsg({ ...errMsg, ipAddressesList: `Hostname is required` });
      valid = false;
    }
    if (formData?.name === "") {
      setErrMsg({ ...errMsg, name: `Name is Required!!` });
      valid = false;
    }
    return valid;
  };

  const submitHandler = async () => {
    const hasErros = Object.values(errMsg).some((err) => err !== "");
    if (handleValidateAlarmGroup() && !hasErros) {
      try {
        let result: any;
        if (authPassword !== authConfirmPassword) {
          setErrMsg({ ...errMsg, authPassword: "Passwords do not match" });
          return;
        }
        if (privPassword !== privConfirmPassword) {
          setErrMsg({ ...errMsg, privPassword: "Passwords do not match" });
          return;
        }
        
        let heartbeatInterval = formData.heartbeatInterval;
        if (heartbeatInterval < 0 || heartbeatInterval > 3600) {
          return;
        }

        if (isCreateSNMPGroup) {
          result = await createSnmpGroup(snmpGroupPayload(formData, version));
        } else {
          result = await updateSnmpGroup(snmpGroupPayload(formData, version));
        }
        if (result?.error) throw result?.error;
        if (result.data?.status === 200) {
          dispatch(setBasicDialogOpenFor(undefined));
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `The data has been ${isCreateSNMPGroup ? "added" : "updated"} for ${titleCase(formData?.name)}.`,
            })
          );
          refetch();
        }
      } catch (error: any) {
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  return {
    data,
    isLoading,
    alarmForwardingColumns,
    getSelectedSnmpGroup,
    selectedGroup,
    setSelectedGroup,
    handleDeleteSnmpGroup,
    refetch,
    formData,
    setFormData,
    errMsg,
    setErrMsg,
    version,
    setVersion,
    AuthenticationOptions,
    PrivacyOptions,
    isAuthChecked,
    setIsAuthChecked,
    isPrivChecked,
    setIsPrivChecked,
    submitHandler,
    isCreateSNMPGroup,
    authConfirmPassword,
    setAuthConfirmPassword,
    privConfirmPassword,
    setPrivConfirmPassword,
    onBlurHandler,
    selectedRowIDs,
    setSelectedRowIDs,
    authPassword, 
    setAuthPassword,
    privPassword, 
    setPrivPassword
  };
};
